import React from 'react';
import './BouquetCard.css';

interface BouquetCardProps {
  id: number;
  name: string;
  description?: string;
  price: number;
  quantity: number;
  createdAt: string;
  updatedAt: string;
  images: string[] | null;
}

// Helper to convert image URL to WebP/AVIF via query params (if your CDN/image server supports it)
// If not, this will gracefully fall back to original format
const getWebPUrl = (url: string) => `${url}?format=webp&quality=80`;
const getAVIFUrl = (url: string) => `${url}?format=avif&quality=70`;

export const BouquetCard: React.FC<BouquetCardProps> = (bouquet) => {
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
    }).format(price);
  };

  // JSON-LD structured data for Product
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": bouquet.name,
    "description": bouquet.description || `Свежий букет ${bouquet.name.toLowerCase()} из цветочного магазина «Ваниль»`,
    "image": bouquet.images && bouquet.images.length ? bouquet.images[0] : `https://vanilufa.ru/og-bouquet-${bouquet.id}.jpg`,
    "sku": `BOUQUET-${bouquet.id}`,
    "offers": {
      "@type": "Offer",
      "url": `https://vanilufa.ru/bouquets/${bouquet.id}`,
      "priceCurrency": "RUB",
      "price": bouquet.price,
      "availability": bouquet.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "LocalBusiness",
        "name": "Цветочный магазин «Ваниль»",
        "telephone": "+7-987-258-81-11",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "ул. Дуговая, 3/3",
          "addressLocality": "Булгаково",
          "addressRegion": "Республика Башкортостан",
          "addressCountry": "RU"
        }
      }
    },
    "brand": {
      "@type": "Brand",
      "name": "Ваниль"
    }
  };

  const primaryImage = bouquet.images && bouquet.images.length ? bouquet.images[0] : null;

  return (
      <article 
        className="bouquet-card"
        itemScope
        itemType="https://schema.org/Product"
        itemID={`https://vanilufa.ru/bouquets/${bouquet.id}`}
      >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <div className="bouquet-image">
        { primaryImage ? (
          <picture>
            <source srcSet={getAVIFUrl(primaryImage)} type="image/avif" />
            <source srcSet={getWebPUrl(primaryImage)} type="image/webp" />
            <img
              src={primaryImage}
              alt={`${bouquet.name} — букет из цветочного магазина «Ваниль»`}
              className="bouquet-img"
              itemProp="image"
              loading="lazy"
              decoding="async"
              width="300"
              height="300"
            />
          </picture>
        ) : (
          <div className="bouquet-placeholder" itemProp="image" content="https://vanilufa.ru/placeholder-bouquet.jpg">🌹</div>
        )}
      </div>
      <div className="bouquet-info">
        <h3 className="bouquet-name" itemProp="name">{bouquet.name}</h3>
        {bouquet.description && (
          <p className="bouquet-description" itemProp="description">{bouquet.description}</p>
        )}
        <div className="bouquet-footer">
          <div className="bouquet-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
            <span itemProp="priceCurrency" content="RUB" />
            <span itemProp="price" content={bouquet.price.toString()}>
              {formatPrice(bouquet.price)}
            </span>
            <meta itemProp="availability" content={bouquet.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"} />
          </div>
          <div className="bouquet-quantity" itemProp="inventoryLevel" itemScope itemType="https://schema.org/QuantitativeValue">
            В наличии: <span itemProp="value">{bouquet.quantity}</span> шт.
          </div>
        </div>
      </div>
    </article>
  );
};

